/**
	* UI Manager
	* Handles: user interfaces: popups, tooltips, transitions, panels
	* @module UIManager
*/

// OpenLayers
import { Units } from "ol/control/ScaleLine";

// Vector2
import Vector2 from "lib/classes/vector2";

// Popup
import { Popup } from "lib/classes/popup";

// jQuery
import $ from "jquery";

// Key popups for easy instancing
import ErrorPopup from "views/popups/Error";
import ConfirmPopup from "views/popups/Confirm"
import LoadingPopup from "views/popups/Loading";
import GoogleMapView from 'views/popups/GoogleMapView';
import iFramePopup from 'views/popups/iFrame';

// Models
import MapToolMode from "lib/enums/maptoolmode";
import {getToolEnabled} from 'lib/utilities/toolmodes';

// Lang
import { getLocaleString } from "lib/localization/vendor";

import ReactTooltip from "react-tooltip";
import styled from "styled-components";

export default class UIManager {
	private popupsOpen:Popup[] = [];
	private scaleLineUnits:Units;
	private topPopupIndex:number = 0;
	private keysDown:Map<string, boolean> = new Map<string, boolean>();
	private keysWhich:Map<number, boolean> = new Map<number, boolean>();
	private properties:Map<string, any> = new Map<string, any>();
	private toolClickMaster:Map<MapToolMode, Function> = new Map<MapToolMode, Function>();
	
	public tooltip:any;
	public mousePosition:Vector2;
	public currentMapToolMode:MapToolMode;
	
	public transitionDownOptions:any = {
		duration: _config.slideTranSpeed
	}
	public transitionUpOptions:any = {
		duration: _config.slideTranSpeed
	}
	public transitionDownOptionsFlex:any = {
		duration: _config.slideTranSpeed,
		start: function() {
			$(this).css("display", "flex");
		}
	}
	
	private rightPanelOpen:boolean = false;
	private rightPanelRegisters:Map<string, boolean> = new Map<any, boolean>();
	
	constructor() {
		// React tool-tip styled using 'styled-components' module
		const StyledTooltip = styled(ReactTooltip)`
			&.type-dark {
				padding-left: 10px;
				padding-right: 10px;
			}
		`;
		this.tooltip = StyledTooltip;
		
		// Capture keyboard input
		window.addEventListener("keydown", (e:any) => {
			this.keysDown.set(e.key, true);
			this.keysWhich.set(e.which, true);
		});
		window.addEventListener("keyup", (e:any) => {
			this.keysDown.set(e.key, false);
			this.keysWhich.set(e.which, false);
		});
	}
	
	/**
		* Get current opened popup
		* 
		* @returns
		* {Popup} current popup
	*/
	public getPopup():Popup {
		return this.popupsOpen?.[0];
	}
	
	/**
		* Get current opened popups
		* 
		* @returns
		* {Popup} opened popups
	*/
	public getPopups():Popup[] {
		return this.popupsOpen;
	}
	
	/**
		* Is popup open
		* 
		* @param {Popup} popup - Popup to check against
		* 
		* @returns
		* {boolean} true/false
	*/
	public isPopupOpen(popup:Popup):boolean {
		return this.popupsOpen.indexOf(popup) > -1;
	}
	
	/**
		* Shows popup
		* 
		* @param {Popup} popup - Popup to show
		* @param {boolean} additive - Should this popup display additively?
	*/
	public showPopup(popup:Popup) {
		this.splicePopup(popup);
			
		this.popupsOpen.push(popup);
			
		_events.invoke("show-popups", this.popupsOpen);
	}
	
	/**
		* Close current opened popup
	*/
	public closePopup() {
		const popup:Popup = this.popupsOpen.shift();
		
		popup.onClose?.();
		
		_events.invoke("show-popups", this.popupsOpen);
	}
	
	/**
		* Close certain opened popup
	*/
	public closePopupOf(popup:Popup) {
		this.splicePopup(popup);
			
		popup.onClose?.();
		
		_events.invoke("show-popups", this.popupsOpen);
	}
	
	/*
		* Splice popup out of open popups array
	*/
	private splicePopup(popup:Popup) {
		const index:number = this.popupsOpen.indexOf(popup);
		
		if(index > -1)
			this.popupsOpen.splice(index, 1);
	}
	
	/**
		* Show error popup
		* 
		* @param {string} error - Error message
		* @param {string} title - Title of error popup
		* @param {Function} onClose - Method to call when popup closed (default null)
	*/
	public showError(error:string, title:string = "", onClose:Function = null) {
		ErrorPopup.set("message", error);
		ErrorPopup.set("title", title || getLocaleString("error"));
		ErrorPopup.onClose = onClose;
		
		this.showPopup(ErrorPopup);
	}
	
	/**
		* Show notice popup
		* 
		* @param {string} notice - Notice message
		* @param {string} title - Title of notice popup
		* @param {Function} onClose - Method to call when popup closed (default null)
	*/
	public showNotice(notice:string, title:string = "", onClose:Function = null) {
		ErrorPopup.set("message", notice);
		ErrorPopup.set("title", title || getLocaleString("notice"));
		ErrorPopup.onClose = onClose;
		
		this.showPopup(ErrorPopup);
	}
	
	/**
		* Show confirm popup
		* 
		* @param {string} message - Message
		* @param {Function} onConfirm - Method to call when confirmed
	*/
	public showConfirm(message:string, onConfirm:Function = null) {
		ConfirmPopup.set("message", message);
		ConfirmPopup.set("onconfirm", onConfirm);
		
		this.showPopup(ConfirmPopup);
	}
	
	/**
		* Show loading popup
	*/
	public showLoading(message:string = "") {
		LoadingPopup.set("message", message);
		
		this.showPopup(LoadingPopup);
	}
	
	/**
		* Hide loading popup
	*/
	public hideLoading() {
		this.closePopupOf(LoadingPopup);
	}
	
	/**
		* Show google street view
		* 
		* @remarks
		* Nice and easy method to open street view with long/lat co-ords
	*/
	public openGoogleMapViewAt(lng:number, lat:number, uri:string = '', options:any = {}) {
		const url:string = uri || `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`;
		
		GoogleMapView.set('lng', lng);
		GoogleMapView.set('lat', lat);
		GoogleMapView.set('url', url);
		GoogleMapView.set('feature', options?.feature);
		GoogleMapView.set('map-mode', options?.mapMode);
		GoogleMapView.set('map-type', options?.mapType);
		
		this.showPopup(GoogleMapView);
	}
	
	/**
		* iFrame popup
		* 
		* @remarks
		* Nice and easy method to open an iframe
	*/
	public openIFrame(url:string, options:any = {}) {
		iFramePopup.set('url', url);
		iFramePopup.set('title', options.title);
		
		this.showPopup(iFramePopup);
	}
	
	/**
		* Auto select a feature and edit
		* 
		* @remarks
		* Timed to work
	*/
	public autoSelectEditFeatures(features:any[]) {
		_ui.setToolMode(MapToolMode.Select);
		
		setTimeout(() => {
			_events.invoke('select-features', features);
			setTimeout(() => _events.invoke('edit-features-panel'), 100);
		}, 50);
	}
	
	/**
		* Is the pointer busy
		* 
		* @remarks
		* If the current map tool mode is the draw tool, consider the pointer
		* to be busy drawing.
		* 
		* @returns
		* {boolean} true/false
	*/
	public isPointerBusy():boolean {
		return this.currentMapToolMode == MapToolMode.Draw || this.currentMapToolMode == MapToolMode.Annotation;
	}
	
	/**
		* Is the right panel open
		* 
		* @remarks
		* Checks registered right panels and if any are open then return true.
		* 
		* @returns
		* {boolean} true/false
	*/
	public getRightPanelOpen():boolean {
		let isOpen:boolean = false;
		
		this.rightPanelRegisters.forEach((value:boolean, key:string) => {
			if(value) isOpen = true;
		});
		
		return isOpen;
	}
	
	/**
		* Get the currently opened right panel caller
		* 
		* @remarks
		* Checks registered right panels and if any are open then return the caller.
		* 
		* @returns
		* {string} The caller of the right panel that is open
	*/
	public getRightPanelOpenCaller():string {
		let caller:string;
		
		this.rightPanelRegisters.forEach((value:boolean, key:string) => {
			if(value) caller = key;
		});
		
		return caller;
	}
	
	/**
		* Gets the selected class for any UI element
		
		* @remarks
		* Depends on if access styling is enabled
		
		* @returns
		* {string} class
	*/
	public getSelClassName():string {
		if(_config.accessStylingEnabled)
			return 'selected-access';
		
		return 'selected';
	}
	
	/**
		* Get if the zoom bar should be available
		* 
		* @returns
		* {boolean} is enabled?
	*/
	public getZoomBarEnabled(mapConfig:any):boolean {
		return getToolEnabled(MapToolMode.MapControl, mapConfig);
	}
	
	/**
		* Set tool mode
		* 
		* @param {MapToolMode} toolMode - Tool mode to change to
		* 
		* @remarks
		* A shortcut method to changing the map tool mode from anywhere
	*/
	public setToolMode(toolMode:MapToolMode) {
		_events.invoke("tool-mode-set", toolMode);
	}
	
	/**
		* Activate tool mode
		* 
		* @param {MapToolMode} toolMode - Tool mode to activate
		* 
		* @remarks
		* This will fully activate the mode, not just update the state
	*/
	public activateToolMode(toolMode:MapToolMode) {
		_events.invoke("tool-mode", toolMode);
	}
	
	/**
		* Register a UI right panel visibility
		* 
		* @param {boolean} open - Visible/invisible?
		* @param {any} caller - Any value that uniquely identifies the caller
	*/
	public registerRightPanel(open:boolean, caller:string) {
		this.rightPanelRegisters.set(caller, open);
		this.rightPanelOpen = this.getRightPanelOpen();
		
		_events.invoke("right-panel-update", this.rightPanelOpen);
	}
	
	/**
		* Is the screen too small for the zoom bar?
		* 
		* @returns {boolean} too small?
	*/
	public tooSmallForZoomBar():boolean {
		return _dimensions.width < 600 || _dimensions.height < 600;
	}
	
	/**
		* Get the scale line units
		* 
		* @returns {Units} units
	*/
	public getScaleLineUnits():Units {
		return this.scaleLineUnits ?? (_config.defaultScaleLineUnits as Units);
	}
	
	/**
		* Set the scale line units
		* 
		* @param {Units} units - Units that the scale line now uses
		* @param {boolean} init - Is this an initialize call? If so, do not call event
	*/
	public setScaleLineUnits(units:Units, init:boolean = false) {
		this.scaleLineUnits = units;
		
		if(!init)
			_events.invoke("scale-line-units-changed", units);
	}
	
	/** 
		* Register popup index 
		* 
		* @param {number} index
	*/
	public registerPopupIndex(index:number) {
		if(this.topPopupIndex < index)
			this.topPopupIndex = index;
	}
	
	/**
		* Get top popup index
		* 
		* @returns {number} top index
	*/
	public getTopPopupIndex():number {
		return this.topPopupIndex;
	}
	
	/*
	
	*/
	/*
		Are any documents elements selected?
	*/
	public hasFocus():boolean {
		const activeElement:Element = document.activeElement;
		const inputs:Array<string> = ["input", "select", "button", "textarea"];
		const inputType:string = activeElement?.attributes?.getNamedItem("type")?.value;
		
		return activeElement && inputs.indexOf(activeElement.tagName.toLowerCase()) !== -1 && inputType != "checkbox";
	}
	
	/**
		* Set the map tool mode click master method
		* 
		* @param {MapToolMode} tool mode
		* @param {Function} handler method
		* 
		* @remarks
		* These methods are defined by the draw method in the ToolPicker view
		* and are not available before drawn. Any tool that isn't drawn
		* won't be stored here.
	*/
	public setMapToolModeClickMaster(toolMode:MapToolMode, method:Function) {
		this.toolClickMaster.set(toolMode, method);
	}
	
	/**
		* Invoke the on click tool master method
		* 
		* @param {MapToolMode} tool mode
		* 
		* @remarks
		* Invoke the tool mode method defined in setMapToolModeClickMaster
	*/
	public invokeMapToolModeClickMaster(toolMode:MapToolMode) {
		const method:Function = this.toolClickMaster.get(toolMode);
		
		method?.();
	}
	
	/* Property methods */
	public get(key:string):any {
		return this.properties.get(key);
	}
	public set(key:string, value:any) {
		this.properties.set(key, value);
	}
	
	/*
		Key controls	
	*/
	public isShiftDown():boolean {
		return !!this.keysDown.get("Shift");
	}
	public isCtrlDown():boolean {
		return this.isControlDown() || this.isMetaDown();
	}
	public isMetaDown():boolean {
		return !!this.keysDown.get("Meta");
	}
	public isControlDown():boolean {
		return !!this.keysDown.get("Control");
	}
	public isSpaceDown():boolean {
		return !!this.keysWhich.get(32);
	}
}